/* eslint-disable react-refresh/only-export-components */

import EditIcon from "@mui/icons-material/Edit";
import EuroIcon from "@mui/icons-material/Euro";
import PaymentsIcon from "@mui/icons-material/Payments";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ProgressBar from "@ramonak/react-progress-bar";
import { formatDate, formatRelative, toDate } from "@repo/utils/date";
import type { ColumnDef } from "@tanstack/react-table";
import type { Delivery } from "@types_def/models/delivery.types";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { RouteTrajectory } from "@/components/ui/route-trajectory";
import { UserRole } from "@/types/models/user.type";
import { TABLE_COLUMN_SIZING } from "@/utils/constants/table-column-sizing";
import { formatEuro } from "@/utils/delivery-pricing";
import { getDeliveryTypeColor } from "@/utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";

import ViewDeliveryButtons from "../view-delivery-buttons";

type TableColumnAlign = "left" | "center" | "right";
type ColumnDefWithAlign<TData> = ColumnDef<TData> & {
  align?: TableColumnAlign;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000;

function toValidDate(value: unknown): Date | null {
  return value instanceof Date || typeof value === "string" ? toDate(value) : null;
}

function dateStringFormat(value: unknown): string {
  return formatDate(toValidDate(value), { fallback: "-" });
}

function isAdmin() {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
}

function formatToken(token: string) {
  return String(token).toLowerCase().replaceAll("_", "-");
}

export function formatDistance(distance: number) {
  return `${distance.toFixed(2)} km`;
}

function deliveryTokens(delivery: Delivery) {
  return {
    st: formatToken(delivery.status),
    tt: formatToken(delivery.type),
    id: delivery.id,
  };
}

// ─── Shared Cell Components ───────────────────────────────────────────────────

export function RemainingTimeCell({
  carDeliveryDeadline,
  createdAt,
}: {
  carDeliveryDeadline: Date | undefined;
  createdAt: Date;
}) {
  const deadlineDate = toValidDate(carDeliveryDeadline);
  const createdAtDate = toValidDate(createdAt);

  if (!deadlineDate || !createdAtDate) return <Typography variant="body2">-</Typography>;

  const now = new Date();
  const hoursLeft = Math.trunc((deadlineDate.getTime() - now.getTime()) / HOUR_MS);
  const totalWindow = Math.trunc((deadlineDate.getTime() - createdAtDate.getTime()) / HOUR_MS);
  const isOverdue = hoursLeft < 0;
  const relative = formatRelative(deadlineDate, { now });

  return (
    <div style={{ width: "100px" }}>
      <Tooltip
        arrow
        placement="top"
        title={
          isOverdue
            ? `La date de livraison était prévue ${relative}`
            : `Livraison prévue ${relative}`
        }
      >
        <div>
          <ProgressBar
            bgColor="#1C6DD0"
            completed={isOverdue ? 0 : hoursLeft}
            height="10px"
            isLabelVisible={false}
            labelColor="#1C6DD0"
            labelSize="inherit"
            maxCompleted={totalWindow}
          />
        </div>
      </Tooltip>
    </div>
  );
}

// ─── Action Components ────────────────────────────────────────────────────────

type ActionButtonProps = {
  to: string;
  text: string;
  icon: React.ReactNode;
  color?: "primary" | "success" | "info" | "warning";
  disabled?: boolean;
};

function ActionStack({ children }: { children: React.ReactNode }) {
  return (
    <Stack direction="row" gap={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
      {children}
    </Stack>
  );
}

function ActionButton({ to, text, icon, color = "primary", disabled = false }: ActionButtonProps) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t(`delivery.action.${text}`)}>
      <IconButton
        component={Link}
        to={to}
        color={color}
        disabled={disabled}
        size="small"
        sx={{
          border: 1,
          borderColor: "divider",
          boxShadow: 1,
          "&:hover": { boxShadow: 2 },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

// ─── Filter Columns ───────────────────────────────────────────────────────────

const FILTER_COLUMNS: ColumnDefWithAlign<Delivery>[] = (
  [
    {
      id: "deliveryId",
      label: "Delivery ID",
      placeholder: "Search by delivery ID",
      accessor: (row: Delivery) => row.publicId ?? "",
    },
    {
      id: "vin",
      label: "VIN",
      placeholder: "Search by VIN",
      accessor: (row: Delivery) => row.vehicles?.[0]?.vin ?? "",
    },
    {
      id: "vehicleCode",
      label: "Plate",
      placeholder: "Search by plate",
      accessor: (row: Delivery) => row.vehicles?.[0]?.vehicleCode ?? "",
    },
  ] as const
).map(({ id, label, placeholder, accessor }) => ({
  id,
  accessorFn: accessor,
  enableColumnFilter: true,
  enableHiding: id !== "deliveryId",
  enableSorting: false,
  meta: { variant: "text", label, placeholder },
  header: ({ column }) => <DataTableColumnHeader column={column} title={label} />,
  cell: ({ row }) => <Typography variant="body2">{accessor(row.original) || "-"}</Typography>,
}));

// ─── Shared Columns ───────────────────────────────────────────────────────────

const CANCELLED_STATUSES = new Set<DeliveryStatus>([
  DeliveryStatus.CANCELLED_BY_CUSTOMER,
  DeliveryStatus.CANCELLED_BY_ADMIN,
  DeliveryStatus.CANCELLED_BY_MANDATORY,
]);

const CONTRACT_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "publicId",
  meta: { label: "Contrat" },
  accessorKey: "publicId",
  enableSorting: true,
  enableHiding: true,
  size: TABLE_COLUMN_SIZING.md,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Contrat" />,
  cell: ({ row }) => (
    <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      {CANCELLED_STATUSES.has(row.original.status) && (
        <Box sx={{ color: "error.main", fontWeight: 700 }}>!</Box>
      )}
      <Box
        sx={{
          borderRadius: "50%",
          width: 20,
          height: 20,
          background: getDeliveryTypeColor(row.original.type),
        }}
      />
      <Box>{row.original.publicId}</Box>
    </Box>
  ),
};

const ORGANIZATION_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "organizationId",
  meta: { label: "Organisation" },
  accessorKey: "organization",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Organisation" />,
  cell: ({ row }) => (
    <Typography variant="body2">{row.original.organization?.name ?? "-"}</Typography>
  ),
};

const ROUTE_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "route",
  meta: { label: "Trajet" },
  accessorKey: "id",
  size: TABLE_COLUMN_SIZING.xxl,
  enableSorting: false,
  enableHiding: false,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Trajet" />,
  cell: ({ row }) => (
    <RouteTrajectory from={row.original.fromAddress} to={row.original.toAddress} />
  ),
};

const DELIVERY_DATE_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "carDeliveryDeadline",
  meta: { label: "Date de livraison" },
  accessorKey: "carDeliveryDeadline",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Date de livraison" />,
  cell: ({ row }) => (
    <Typography variant="body2">{dateStringFormat(row.original.carDeliveryDeadline)}</Typography>
  ),
};

const CLIENT_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "clientLastName",
  meta: { label: "Client" },
  accessorKey: "clientLastName",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Client" />,
  cell: ({ row }) => <Typography variant="body2">{row.original.clientLastName ?? "-"}</Typography>,
};

const DRIVER_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "driver",
  meta: { label: "Driver" },
  accessorKey: "selectedDriver",
  enableSorting: false,
  enableHiding: false,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Driver" />,
  cell: ({ row }) => (
    <Typography variant="body2">
      {row.original.selectedDriver?.fullName ?? row.original.acceptedOffer?.driver?.fullName ?? "-"}
    </Typography>
  ),
};

const DISTANCE_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "distance",
  meta: { label: "Distance totale" },
  accessorKey: "distance",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Distance totale" />,
  cell: ({ row }) => (
    <Typography variant="body2">{formatDistance(row.original.distance)}</Typography>
  ),
};

const CLIENT_PRICE_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "clientFinalPrice",
  meta: { label: "Montant Client" },
  accessorKey: "clientFinalPrice",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Montant Client" />,
  cell: ({ row }) => (
    <Typography variant="body2">
      {row.original.clientFinalPrice != null ? formatEuro(+row.original.clientFinalPrice) : "P/A"}
    </Typography>
  ),
};
// ─── Table Column Sets ────────────────────────────────────────────────────────

function withBase(cols: ColumnDefWithAlign<Delivery>[]) {
  return [...FILTER_COLUMNS, CONTRACT_COLUMN, ORGANIZATION_COLUMN, ...cols];
}

// Used by: requested, confirmed-by-customer, pending-customer-confirmation, rescheduled-by-admin
export const REQUESTED_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  CLIENT_COLUMN,
  { ...DELIVERY_DATE_COLUMN, enableColumnFilter: false },
  CLIENT_PRICE_COLUMN,
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const path = isAdmin() ? "/admin/pilot" : "/client/pilot";
      return (
        <ActionButton
          to={`${path}/${tt}/${st}/${id}`}
          text="details"
          icon={<VisibilityIcon fontSize="small" />}
        />
      );
    },
  },
];

export const CONFIRMED_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  DELIVERY_DATE_COLUMN,
  DISTANCE_COLUMN,
  // {
  //   id: "clientInitialPrice",
  //   meta: { label: "Montant Client" },
  //   accessorKey: "clientInitialPrice",
  //   enableSorting: true,
  //   enableHiding: true,
  //   header: ({ column }) => <DataTableColumnHeader column={column} title="Montant Client" />,
  //   cell: ({ row }) => (
  //     <Typography variant="body2">
  //       {row.original.clientInitialPrice != null ? formatEuro(+row.original.clientInitialPrice) : "P/A"}
  //     </Typography>
  //   ),
  // },
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: isAdmin() ? TABLE_COLUMN_SIZING.md : TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const basePath = isAdmin() ? "/admin/pilot" : "/client/pilot";
      if (!isAdmin()) {
        return (
          <ActionButton
            to={`${basePath}/${tt}/${st}/${id}`}
            text="details"
            icon={<VisibilityIcon fontSize="small" />}
          />
        );
      } else {
        return (
          <ActionStack>
            <ActionButton
              color="success"
              to={`${basePath}/${tt}/${st}/${id}`}
              text="view-details"
              icon={<VisibilityIcon fontSize="small" />}
            />
            <ActionButton
              to={`/admin/pilot/create-deal/${st}/${tt}/${id}`}
              color="primary"
              text="send-deal"
              icon={<SendIcon fontSize="small" />}
            />
          </ActionStack>
        );
      }
    },
  },
];

export const WAITING_FOR_DRIVER_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  CLIENT_COLUMN,
  DELIVERY_DATE_COLUMN,
  {
    id: "remainingTime",
    meta: { label: "Temps restant" },
    accessorKey: "remainingHoursToEstimatedDate",
    enableSorting: false,
    enableHiding: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Temps restant" />,
    cell: ({ row }) => (
      <RemainingTimeCell
        carDeliveryDeadline={row.original.carDeliveryDeadline}
        createdAt={row.original.createdAt}
      />
    ),
  },
  {
    id: "averageOffersPrice",
    meta: { label: "Prix moyen" },
    accessorKey: "averageOffersPrice",
    enableSorting: false,
    size: TABLE_COLUMN_SIZING.lg,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Prix moyen" />,
    cell: ({ row }) => {
      return (
        <Typography variant="body2">
          {row.original.averageOffersPrice != null
            ? formatEuro(+row.original.averageOffersPrice)
            : "P/A"}
        </Typography>
      );
    },
  },
  {
    id: "number_of_offers",
    meta: { label: "Nombre d'offres" },
    accessorKey: "number_of_offers",
    enableSorting: false,
    enableHiding: true,
    size: TABLE_COLUMN_SIZING.lg,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Nombre d'offres" />,
    cell: ({ row }) => (
      <Typography variant="body2">{row.original.number_of_offers ?? "-"}</Typography>
    ),
  },
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: isAdmin() ? TABLE_COLUMN_SIZING.lg : TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const basePath = isAdmin() ? "/admin/pilot" : "/client/pilot";
      if (!isAdmin())
        return (
          <ActionButton
            to={`${basePath}/${tt}/${st}/${id}`}
            text="view-request"
            icon={<VisibilityIcon fontSize="small" />}
          />
        );
      return (
        <ActionStack>
          <ActionButton
            color="info"
            to={`${basePath}/${tt}/${st}/${id}`}
            text="view-details"
            icon={<VisibilityIcon fontSize="small" />}
          />
          <ActionButton
            to={`/admin/pilot/edit-deal/${st}/${tt}/${id}`}
            color="warning"
            text="edit-offer"
            icon={<EuroIcon fontSize="small" />}
          />
          <ActionButton
            to={`/admin/pilot/delivery-table/${st}/${id}/offers`}
            text="view-offers"
            icon={<PaymentsIcon fontSize="small" />}
            disabled={row.original.number_of_offers === 0}
          />
        </ActionStack>
      );
    },
  },
];

export const ASSIGNED_TO_DRIVER_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  CLIENT_COLUMN,
  ROUTE_COLUMN,
  DRIVER_COLUMN,
  DELIVERY_DATE_COLUMN,
  DISTANCE_COLUMN,
  {
    id: "finalPrice",
    meta: { label: "Prix" },
    accessorKey: "finalPrice",
    enableSorting: true,
    enableHiding: true,
    size: TABLE_COLUMN_SIZING.md,

    header: ({ column }) => <DataTableColumnHeader column={column} title="Prix" />,
    cell: ({ row }) => (
      <Typography variant="body2">
        {row.original.finalPrice != null ? formatEuro(+row.original.finalPrice) : "P/A"}
      </Typography>
    ),
  },
  {
    id: "startCode",
    meta: { label: "Code Départ" },
    accessorKey: "startCode",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.lg,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Code Départ" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.startCode ?? "-"}</Typography>,
  },
  {
    id: "destinationCode",
    meta: { label: "Code Arrivée" },
    accessorKey: "destinationCode",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.lg,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Code Arrivée" />,
    cell: ({ row }) => (
      <Typography variant="body2">{row.original.destinationCode ?? "-"}</Typography>
    ),
  },
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const base = isAdmin() ? "admin" : "client";
      return (
        <ActionButton
          color="success"
          to={`/${base}/pilot/${tt}/${st}/${id}`}
          text="view-deal"
          icon={<VisibilityIcon fontSize="small" />}
        />
      );
    },
  },
];

export const ONGOING_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  {
    id: "status",
    meta: { label: "Status" },
    accessorKey: "status",
    enableSorting: true,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Typography variant="body2">{i18next.t(`delivery.status.${row.original.status}`)}</Typography>
    ),
  },
  DELIVERY_DATE_COLUMN,
  CLIENT_COLUMN,
  ROUTE_COLUMN,
  DRIVER_COLUMN,
  DISTANCE_COLUMN,
  {
    id: "id",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const base = isAdmin() ? "admin" : "client";
      return (
        <ActionStack>
          <ActionButton
            to={`/${base}/pilot/${tt}/${st}/${id}`}
            text="details"
            icon={<VisibilityIcon fontSize="small" />}
          />
        </ActionStack>
      );
    },
  },
];

const CANCELLED_DATE_COLUMN: ColumnDefWithAlign<Delivery> = {
  id: "cancelledAt",
  meta: { label: "Annulé à" },
  accessorKey: "cancelledAt",
  enableSorting: true,
  enableHiding: true,
  header: ({ column }) => <DataTableColumnHeader column={column} title="Annulé à" />,
  cell: ({ row }) => (
    <Typography variant="body2">{dateStringFormat(row.original.cancelledAt)}</Typography>
  ),
};

export const CANCELLED_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  CANCELLED_DATE_COLUMN,
  {
    id: "status",
    meta: { label: "Annule par" },
    accessorKey: "status",
    enableSorting: true,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Annuler par" />,
    cell: ({ row }) => (
      <Typography variant="body2">{i18next.t(`delivery.status.${row.original.status}`)}</Typography>
    ),
  },
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const base = isAdmin() ? "admin" : "client";
      return (
        <ActionStack>
          <ActionButton
            to={`/${base}/pilot/${tt}/${st}/${id}`}
            text="view-details"
            icon={<VisibilityIcon fontSize="small" />}
          />
          <ViewDeliveryButtons
            id={row.original.id}
            status={row.original.status}
            type={row.original.type}
            isAdmin={isAdmin()}
            iconOnly
            cancelledAt={row.original.cancelledAt}
            smsSentAt={row.original.smsSentAt}
          />
        </ActionStack>
      );
    },
  },
];

export const ALERT_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  DELIVERY_DATE_COLUMN,
  DISTANCE_COLUMN,
  CLIENT_PRICE_COLUMN,
  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: isAdmin() ? TABLE_COLUMN_SIZING.md : TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      if (!isAdmin())
        return (
          <ActionButton
            to={`/client/pilot/${tt}/${st}/${id}`}
            text="view-request"
            icon={<VisibilityIcon fontSize="small" />}
          />
        );
      return (
        <ActionStack>
          <ActionButton
            color="success"
            to={`/admin/pilot/${tt}/${st}/${id}`}
            text="view-details"
            icon={<VisibilityIcon fontSize="small" />}
          />
          <ActionButton
            to={`/admin/pilot/edit-deal/alert/${tt}/${id}`}
            text="edit-offer"
            icon={<EditIcon fontSize="small" />}
          />
        </ActionStack>
      );
    },
  },
];

export const COMPLETED_DELIVERY_COLUMNS: ColumnDefWithAlign<Delivery>[] = [
  ROUTE_COLUMN,
  {
    id: "completedAt",
    meta: { label: "Validé le" },
    accessorKey: "completedAt",
    enableSorting: true,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Validé le" />,
    cell: ({ row }) => (
      <Typography variant="body2">{dateStringFormat(row.original.completedAt)}</Typography>
    ),
  },
  {
    id: "finalPrice",
    meta: { label: "Prix Final Driver" },
    accessorKey: "finalPrice",
    enableSorting: true,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Prix Final Driver" />,
    cell: ({ row }) => (
      <Typography variant="body2">
        {row.original.finalPrice != null ? formatEuro(+row.original.finalPrice) : "P/A"}
      </Typography>
    ),
  },
  {
    id: "margin",
    meta: { label: "Margin" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.md,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Margin" />,
    cell: ({ row }) => (
      <Typography variant="body2">
        {formatEuro((row.original.clientFinalPrice ?? 0) - (row.original.finalPrice ?? 0))}
      </Typography>
    ),
  },
  {
    id: "clientFinalPrice",
    meta: { label: "Montant HT Client" },
    accessorKey: "clientFinalPrice",
    enableSorting: true,
    enableHiding: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Montant HT Client" />,
    cell: ({ row }) => (
      <Typography variant="body2">
        {row.original.clientFinalPrice != null ? formatEuro(+row.original.clientFinalPrice) : "P/A"}
      </Typography>
    ),
  },
  DELIVERY_DATE_COLUMN,
  {
    id: "rated",
    meta: { label: "Satisfaction" },
    accessorKey: "rated",
    enableSorting: true,
    enableHiding: true,
    size: TABLE_COLUMN_SIZING.md,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Satisfaction" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.rated ? "Yes" : "No"}</Typography>,
  },

  {
    id: "action",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    size: TABLE_COLUMN_SIZING.sm,
    enableHiding: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const { st, tt, id } = deliveryTokens(row.original);
      const to = isAdmin()
        ? `/admin/pilot/details/${tt}/${st}/${id}`
        : `/client/pilot/${tt}/${st}/${id}`;
      return <ActionButton to={to} text="details" icon={<VisibilityIcon fontSize="small" />} />;
    },
  },
];

// ─── Route → Columns Map ──────────────────────────────────────────────────────

export type DeliveryTableRouteKey =
  | "alert"
  | "cancelled"
  | "cancelled-by-customer"
  | "completed"
  | "confirmed"
  | "confirmed-by-customer"
  | "ongoing"
  | "pending-customer-confirmation"
  | "requested"
  | "rescheduled-by-admin"
  | "set"
  | "waiting-for-driver";

export const DELIVERY_TABLE_COLUMNS_BY_ROUTE: Record<
  DeliveryTableRouteKey,
  ColumnDefWithAlign<Delivery>[]
> = {
  alert: withBase(ALERT_DELIVERY_COLUMNS),
  cancelled: withBase(CANCELLED_DELIVERY_COLUMNS),
  "cancelled-by-customer": withBase(CANCELLED_DELIVERY_COLUMNS),
  completed: withBase(COMPLETED_DELIVERY_COLUMNS),
  confirmed: withBase(CONFIRMED_DELIVERY_COLUMNS),
  "confirmed-by-customer": withBase(REQUESTED_DELIVERY_COLUMNS),
  ongoing: withBase(ONGOING_DELIVERY_COLUMNS),
  "pending-customer-confirmation": withBase(REQUESTED_DELIVERY_COLUMNS),
  requested: withBase(REQUESTED_DELIVERY_COLUMNS),
  "rescheduled-by-admin": withBase(REQUESTED_DELIVERY_COLUMNS),
  set: withBase(ASSIGNED_TO_DRIVER_DELIVERY_COLUMNS),
  "waiting-for-driver": withBase(WAITING_FOR_DRIVER_DELIVERY_COLUMNS),
};

export function getDeliveryTableColumnsByRole(
  role: UserRole,
  route: DeliveryTableRouteKey
): ColumnDefWithAlign<Delivery>[] {
  const baseColumns =
    DELIVERY_TABLE_COLUMNS_BY_ROUTE[route] ?? DELIVERY_TABLE_COLUMNS_BY_ROUTE.confirmed;
  const excludeByRoleAndRoute: Partial<Record<UserRole, Record<string, string[]>>> = {
    [UserRole.CLIENT]: {
      ongoing: ["driver", "status", "organizationId"],
      completed: ["completedAt", "finalPrice", "rated", "organizationId", "margin"],
      confirmed: ["organizationId"],
      cancelled: ["organizationId", "status"],
      "cancelled-by-customer": ["organizationId", "status"],
      requested: ["organizationId", "clientLastName", "clientFinalPrice"],
      "confirmed-by-customer": ["clientFinalPrice"],
      "pending-customer-confirmation": ["clientFinalPrice"],
      "rescheduled-by-admin": ["clientFinalPrice"],
    },
    [UserRole.ADMIN]: {
      requested: ["clientFinalPrice"],
      ongoing: ["distance"],
      completed: ["carDeliveryDeadline"],
    },
  };

  const exclude = excludeByRoleAndRoute[role]?.[route] ?? [];
  return baseColumns.filter((col) => !exclude.includes(col.id!));
}
